import flatMap from "lodash/flatMap";
import isEmpty from "lodash/isEmpty";
import slice from "lodash/slice";
import xor from "lodash/xor";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
const initialHistory = { steps: [], current: 0, lostSteps: [] };
export default function useHistory() {
    const [history, setHistory] = useState(initialHistory);
    const deletedRef = useRef([]);
    const undo = useCallback(() => {
        const lastStepIndex = history.current - 1;
        const lastStep = history.steps[lastStepIndex];
        const canUndo = lastStepIndex > 0;
        setHistory(prev => ({
            ...prev,
            current: lastStepIndex
        }));
        return lastStep && lastStep.action === "update"
            ? { ids: lastStep.ids, modifiedData: lastStep.prevData, canUndo }
            : { canUndo };
    }, [history]);
    const redo = useCallback(() => {
        const nextStep = history.steps[history.current];
        const canRedo = history.current + 1 < history.steps.length;
        setHistory(prev => ({
            ...prev,
            current: prev.current + 1
        }));
        return nextStep && nextStep.action === "update"
            ? { ids: nextStep.ids, modifiedData: nextStep.data, canRedo }
            : { canRedo };
    }, [history]);
    const clearHistory = useCallback(() => {
        setHistory(initialHistory);
    }, []);
    const addToHistory = useCallback((action, ids, prevData, data) => {
        if (action === "add" || action === "delete") {
            setHistory(prev => ({
                steps: [...slice(prev.steps, 0, prev.current), { ids, action }],
                current: prev.current + 1,
                lostSteps: [...prev.lostSteps, ...slice(prev.steps, prev.current, prev.steps.length)]
            }));
        }
        if (action === "update") {
            setHistory(prev => {
                const lastStep = prev.current > 0 ? prev.steps[prev.current - 1] : undefined;
                if (lastStep && lastStep.action === "update" && isEmpty(xor(lastStep.ids, ids))) {
                    return {
                        steps: [...slice(prev.steps, 0, prev.current - 1), { ...lastStep, data }],
                        current: prev.current,
                        lostSteps: [
                            ...prev.lostSteps,
                            ...slice(prev.steps, prev.current - 1, prev.steps.length)
                        ]
                    };
                }
                return {
                    steps: [...slice(prev.steps, 0, prev.current), { ids, action, prevData, data }],
                    current: prev.current + 1,
                    lostSteps: [...prev.lostSteps, ...slice(prev.steps, prev.current, prev.steps.length)]
                };
            });
        }
    }, []);
    const deleted = useMemo(() => [
        ...flatMap(history.lostSteps, s => (s.action === "add" ? s.ids : [])),
        ...flatMap(history.steps, (s, ind) => {
            if (ind < history.current)
                return s.action === "delete" ? s.ids : [];
            return s.action === "add" ? s.ids : [];
        })
    ], [history]);
    useEffect(() => {
        deletedRef.current = deleted;
    }, [deleted]);
    return {
        history,
        undo: history.current > 0 ? undo : undefined,
        redo: history.current < history.steps.length ? redo : undefined,
        clearHistory,
        deleted,
        deletedRef,
        addToHistory
    };
}
